// src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

// Create the context
export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Helper to extract admin status from session
  const extractAdminStatus = (session) => {
    try {
      const accessTokenGroups = session.getAccessToken().payload['cognito:groups'] || [];
      const idTokenGroups = session.getIdToken().payload['cognito:groups'] || [];
      
      // Combine both token groups and check case-insensitively
      const allGroups = [...new Set([...accessTokenGroups, ...idTokenGroups])];
      
      console.log('All Cognito groups found:', allGroups);
      
      const isUserAdmin = allGroups.some(group => 
        group.toLowerCase() === 'admin'
      );
      
      console.log('Is user admin:', isUserAdmin);
      return isUserAdmin;
    } catch (err) {
      console.error('Error extracting admin status:', err);
      return false;
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log("Checking authentication status...");
        const user = await Auth.currentAuthenticatedUser();
        console.log("User authenticated:", user);
        
        setCurrentUser(user);
        setIsAuthenticated(true);
        
        // Check for admin privileges by checking Cognito groups
        try {
          const session = await Auth.currentSession();
          const isUserAdmin = extractAdminStatus(session);
          setIsAdmin(isUserAdmin);
        } catch (err) {
          console.error('Error checking admin status:', err);
          setIsAdmin(false);
        }
      } catch (err) {
        console.log("Not authenticated:", err);
        setCurrentUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    setError(null);
    try {
      console.log("Attempting login for:", email);
      const user = await Auth.signIn(email, password);
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Check for admin privileges after login
      try {
        const session = await Auth.currentSession();
        const isUserAdmin = extractAdminStatus(session);
        setIsAdmin(isUserAdmin);
      } catch (err) {
        console.error('Error checking admin status after login:', err);
        setIsAdmin(false);
      }
      
      return user;
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || 'Failed to sign in');
      throw err;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await Auth.signOut();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to sign out');
      throw err;
    }
  };

  // Password reset request
  const resetPassword = async (email) => {
    try {
      return await Auth.forgotPassword(email);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password');
      throw err;
    }
  };

  // Complete password reset
  const completePasswordReset = async (email, code, newPassword) => {
    try {
      return await Auth.forgotPasswordSubmit(email, code, newPassword);
    } catch (err) {
      console.error('Complete password reset error:', err);
      setError(err.message || 'Failed to complete password reset');
      throw err;
    }
  };

  // Sign up function
  const signup = async (userData) => {
    try {
      const { name, email, password } = userData;
      const { user } = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          name
        }
      });
      return user;
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to sign up');
      throw err;
    }
  };

  // Update user profile
  const updateProfile = async (attributes) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, attributes);
      setCurrentUser({ 
        ...currentUser, 
        attributes: { ...currentUser.attributes, ...attributes } 
      });
      return true;
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.message || 'Failed to update profile');
      throw err;
    }
  };

  // Value object provided to consumers
  const value = {
    currentUser,
    isAuthenticated,
    isAdmin,
    isLoading,
    error,
    login,
    logout,
    signup,
    resetPassword,
    completePasswordReset,
    updateProfile
  };

  // Don't render children until auth check is complete
  // This prevents the flash of non-admin content
  if (isLoading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
